/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Typography,
  Box,
  CircularProgress,
  Divider,
  Snackbar,
  Chip
} from "@mui/material";

import Header from "components/Header/Header";
import HeaderLinks from "components/Header/HeaderLinks";
import Footer from "components/Footer/Footer";
import Parallax from "components/Parallax/Parallax";

import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";

import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";
import { FirebaseConfig } from "../config/FirebaseConfig";

import {
  ALPHABET,
  DISTRICTS,
  AIRPORT_LAT_LNG,
  AIRPORT_TRANSLATIONS,
  APPLE_LINK,
  PLAY_LINK,
  POPULAR_ATTRACTIONS,
  UI_TEXT,
  LANGUAGES,
  TRANSLATIONS
} from "./transferConstants";

/* ================= FIREBASE ================= */
const app = initializeApp(FirebaseConfig);
const db = getFirestore(app);

/* ================= LEAFLET FIX ================= */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png"
});

/* ================= HELPERS ================= */
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function formatHotelName(p) {
  if (!p?.name) return "";
  if (!/hotel/i.test(p.name)) return `Hotel ${p.name}`;
  return p.name;
}

// ✅ FIX: avoid double "(Schwechat)"
function airportName(lang) {
  const base =
    AIRPORT_TRANSLATIONS?.[lang] ||
    AIRPORT_TRANSLATIONS?.en ||
    "Vienna International Airport";
  if (/\(Schwechat\)/i.test(base)) return base;
  return `${base} (Schwechat)`;
}


function ui(key, lang) {
  const v = UI_TEXT?.[key];
  return typeof v === "object" ? v[lang] || v.en : v;
}
/* ================= COMPONENT ================= */
export default function Transfer() {
  const { "*": slug } = useParams();
  const qs = useQuery();
  const lang = qs.get("lang") || "en";
   
  const T = TRANSLATIONS[lang] || TRANSLATIONS.en;
  const [expandFrom, setExpandFrom] = useState(false);

  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("overview"); // overview | letter | district | transfer
  const [hotels, setHotels] = useState([]);
  const [transfer, setTransfer] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [popularAttractions, setPopularAttractions] = useState([]);
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeDistanceKm, setRouteDistanceKm] = useState(null);
  const [routeDurationMin, setRouteDurationMin] = useState(null);
  const [toast, setToast] = useState(false);
 
  const [expandedNearby, setExpandedNearby] = useState({});
  const [expandedPopular, setExpandedPopular] = useState({});
function formatPlace(p) {
  if (!p) return "";
  if (p.type === "airport") return airportName(lang);
  if (p.type === "hotel") return formatHotelName(p);
  return p.name;
}
function getPlaceDescription(place, lang) {
  if (!place) return null;

  // 1️⃣ Neue mehrsprachige Beschreibungen
  if (place.descriptions && typeof place.descriptions === "object") {
    if (place.descriptions[lang]) return place.descriptions[lang];
    if (place.descriptions.en) return place.descriptions.en;
  }

  // 2️⃣ Fallback: alte Legacy description
  if (typeof place.description === "string" && place.description.trim()) {
    return place.description;
  }

  // 3️⃣ nichts vorhanden
  return null;
}
const [fromPlace, setFromPlace] = useState(null);
const [toPlace, setToPlace] = useState(null);

function getPopularFrom(transfer) {
  // hotel → popular
  if (transfer.from?.type === "hotel") return transfer.from;

  // airport → hotel → popular (use hotel)
  if (transfer.from?.type === "airport" && transfer.to?.type === "hotel") {
    return transfer.to;
  }

  // popular → popular
  if (transfer.from?.type === "popular") return transfer.from;

  return null;
}


  /* ================= LOAD ================= */
  useEffect(() => {
    async function load() {
      setLoading(true);

      if (!slug) {
        setMode("overview");
        setLoading(false);
        return;
      }
       


      if (/^[A-Z]$/i.test(slug)) {
        setMode("letter");
        const snap = await getDocs(
          query(collection(db, "places"), where("type", "==", "hotel"))
        );
        setHotels(
          snap.docs
            .map(d => d.data())
            .filter(h => h.name?.toUpperCase().startsWith(slug.toUpperCase()))
        );
        setLoading(false);
        return;
      }

      if (/^\d{4}$/.test(slug)) {
        setMode("district");
        const snap = await getDocs(
          query(
            collection(db, "places"),
            where("type", "==", "hotel"),
            where("postcode", "==", slug)
          )
        );
        setHotels(snap.docs.map(d => d.data()));
        setLoading(false);
        return;
      }

      setMode("transfer");

      const snap = await getDoc(doc(db, "transfer_cache", slug));
      if (!snap.exists()) {
        setLoading(false);
        return;
      }

      const t = snap.data();
      setTransfer(t);

      /* 🔽 LOAD DESCRIPTIONS – ADDED */
      if (t.from?.slug) {
  const f = await getDoc(doc(db, "places", t.from.slug));
  if (f.exists()) setFromPlace(f.data());
}

if (t.to?.slug) {
  const to = await getDoc(doc(db, "places", t.to.slug));
  if (to.exists()) setToPlace(to.data());
}


      const fromLat = t.from?.lat ?? AIRPORT_LAT_LNG[0];
      const fromLng = t.from?.lng ?? AIRPORT_LAT_LNG[1];

      fetch(
        `https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${t.to.lng},${t.to.lat}?overview=full&geometries=geojson`
      )
        .then(r => r.json())
        .then(d => {
          const r = d.routes?.[0];
          if (!r) return;
          setRouteCoords(r.geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setRouteDistanceKm((r.distance / 1000).toFixed(1));
          setRouteDurationMin(Math.round(r.duration / 60));
        });

      const hotelSnap = await getDoc(doc(db, "places", t.to.slug));
      if (hotelSnap.exists()) {
        const h = hotelSnap.data();
        if (Array.isArray(h.nearby_places)) {
          const res = await Promise.all(
            h.nearby_places.map(s => getDoc(doc(db, "places", s)))
          );
          setNearbyPlaces(res.filter(r => r.exists()).map(r => r.data()));
        }
      }

      setPopularAttractions(POPULAR_ATTRACTIONS);
      setLoading(false);
    }

    load();
  }, [slug, lang]);

  // ❌ don't block Helmet
 


   

  const fallbackTitle = slug
  ? `Taxi transfer ${slug.replace(/-/g, " ")} | Ryyd`
  : `Taxi transfers in Vienna | Ryyd`;
const fallbackDesc = slug 
    ? `Professional taxi and airport transfer service for ${slug.replace(/-/g, " ")}. Fixed prices, premium cars.`
    : `Reliable airport transfers and taxi services in Vienna. Book your ride online.`;
const seoTitle =
  transfer
    ? `${T.from} ${formatPlace(transfer.from)} ${T.to} ${formatPlace(transfer.to)}
 – ${transfer.to.address || ""} ${transfer.to.postcode || ""} | Ryyd`
    : fallbackTitle;
const seoDesc = transfer
    ? `Book your transfer from ${formatPlace(transfer.from)} to ${formatPlace(transfer.to)}. Distance: ${routeDistanceKm || ''}km. Best rates and professional drivers.`
    : fallbackDesc;
return (
  <div style={{ margin: "-8px" }}>
    {/* ✅ ONE Helmet ONLY */}
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
    </Helmet>

    {loading ? (
      <Box mt={10} textAlign="center">
        <CircularProgress />
      </Box>
    ) : (
      <>
        <Header color="transparent" rightLinks={<HeaderLinks />} fixed />
        <Parallax small filter image={require("assets/img/header-back.jpg")} />

      <Box sx={{ background: "#fff", mt: 6, maxWidth: 1200, mx: "auto", p: 4, borderRadius: 3 }}>

        {/* A–Z */}
        <Box mb={2}>
          {ALPHABET.map(l => (
            <Link key={l} to={`/transfer/${l}?lang=${lang}`} style={{ marginRight: 8 }}>
              {l}
            </Link>
          ))}
        </Box>

        {/* DISTRICTS */}
        <Box mb={3}>
          {DISTRICTS.map(d => (
            <Link key={d} to={`/transfer/${d}?lang=${lang}`} style={{ marginRight: 12 }}>
              {d}
            </Link>
          ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* OVERVIEW */}
        {mode === "overview" && (
          <>
            <Typography variant="h4" gutterBottom>{T.overview}</Typography>
            <Typography>{T.districts}</Typography>
          </>
        )}

        {/* LETTER / DISTRICT LIST */}
        {(mode === "letter" || mode === "district") && (
          <>
            <Typography variant="h4" gutterBottom>{T.overview}</Typography>
            {hotels.map(h => (
              <Box key={h.slug}>
                🚕 <Link to={`/transfer/vienna-airport-to-${h.slug}?lang=${lang}`}>
                  {formatHotelName(h)}
                </Link>
              </Box>
            ))}
          </>
        )}

        {/* TRANSFER */}
        {mode === "transfer" && transfer && (
          <>
            <Typography variant="h4" gutterBottom>
              {T.from} {formatPlace(transfer.from)} {T.to} {formatPlace(transfer.to)}
            </Typography>

            <Typography color="textSecondary">
              📍 {transfer.to.address} {transfer.to.postcode}
            </Typography>

            {fromPlace && transfer.from?.type !== "airport" && (
  <Box mb={2}>
    <Typography variant="h6">
      🏨 {formatPlace(transfer.from)}
    </Typography>

    {expandFrom && (
      <Typography color="textSecondary">
        {getPlaceDescription(fromPlace, lang)}
      </Typography>
    )}

    {getPlaceDescription(fromPlace, lang) && (
      <Link
        to="#"
        onClick={e => {
          e.preventDefault();
          setExpandFrom(v => !v);
        }}
        style={{ fontSize: 13 }}
      >
        {expandFrom ? "❌" : "👉"}
      </Link>
    )}
  </Box>
)}



            {toPlace && (
  <Box mb={2}>
    <Typography variant="h6">
      📍 {formatPlace(transfer.to)}
    </Typography>

    <Typography color="textSecondary">
      {getPlaceDescription(toPlace, lang)}
    </Typography>
  </Box>
)}


            <Box sx={{ height: 520, my: 3 }}>
              <MapContainer
                center={[transfer.to.lat, transfer.to.lng]}
                zoom={16}
                style={{ height: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <Marker position={[transfer.from.lat, transfer.from.lng]}>
  <Popup>{formatPlace(transfer.from)}</Popup>
</Marker>

<Marker position={[transfer.to.lat, transfer.to.lng]}>
  <Popup>{formatPlace(transfer.to)}</Popup>
</Marker>


                {nearbyPlaces.map(p => (
                  <Marker key={p.slug} position={[p.lat, p.lng]}>
                    <Popup>
                      <b>{p.name}</b>
                      {p.website && (
                        <div>
                          <a href={p.website} target="_blank" rel="nofollow noopener">
                            🌐 Website
                          </a>
                        </div>
                      )}
                    </Popup>
                  </Marker>
                ))}

                {popularAttractions.map(p => (
  <Marker key={p.slug} position={[p.lat, p.lng]}>
    <Popup>
      <b>{p.name}</b>
      <div>{p.address}</div>
    </Popup>
  </Marker>
))}


                {routeCoords.length > 0 && (
                  <Polyline positions={routeCoords} pathOptions={{ color: "#1976d2", weight: 5 }} />
                )}
              </MapContainer>
            </Box>

            <Box
              sx={{ background: "#f6f7f9", p: 2, borderRadius: 2, cursor: "pointer" }}
              onClick={() => setToast(true)}
            >
              ⏱ {routeDistanceKm ?? "—"} km · {routeDurationMin ?? "—"} min · 💶 {T.checkPrice}
            </Box>

            {nearbyPlaces.length > 0 && (
  <>
    <Divider sx={{ my: 3 }} />
    <Typography variant="h6">
      🏛 {ui("nearbyPlaces", lang)}
    </Typography>

    {nearbyPlaces.map(p => {
  const expanded = expandedNearby[p.slug];
  //const hasDesc =    typeof p.description === "string" && p.description.trim().length > 0;
const desc = getPlaceDescription(p, lang);
const hasDesc = !!desc;

  return (
    <Box key={p.slug} sx={{ mb: 0.5 }}>
      {/* SINGLE LINE */}
      <Typography variant="body2">
        • <strong>{p.name}</strong>

        {hasDesc && (
          <>
            {" "}
            <Link
              to="#"
              onClick={e => {
                e.preventDefault();
                setExpandedNearby(prev => ({
                  ...prev,
                  [p.slug]: !expanded
                }));
              }}
              style={{ fontSize: 12 }}
            >
              {expanded ? "❌" : "👉"}
            </Link>
          </>
        )}

        {p.website && (
          <>
            {" "}
            <a
              href={p.website}
              target="_blank"
              rel="nofollow noopener"
              style={{ fontSize: 12 }}
            >
              🌐
            </a>
          </>
        )}
      </Typography>

      {/* EXPANDED DESCRIPTION (ONLY WHEN CLICKED) */}
      
{expanded && desc && (
  <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
    {desc}
  </Typography>
)}

    </Box>
  );
})}

  </>
)}

{popularAttractions.length > 0 && (
  <>
    <Divider sx={{ my: 3 }} />
    <Typography variant="h6">
      🎯 {ui("popularFromHotel", lang)} {formatHotelName(transfer.to)}
    </Typography>

    {popularAttractions.map(p => {
      const fromPlace = getPopularFrom(transfer);
      if (!fromPlace) return null;

      const fromSlug =
        fromPlace.type === "hotel"
          ? `hotel-${fromPlace.slug}`
          : `popular-${fromPlace.slug}`;

      const pairSlug = `${fromSlug}-to-${p.slug}`;
      const expanded = expandedPopular[p.slug];
const desc = getPlaceDescription(p, lang);
const hasDesc = !!desc;

      return (
        <Box key={p.slug} mb={1}>
          🚕{" "}
          <Link to={`/transfer/${pairSlug}?lang=${lang}`}>
            {ui("taxiFrom", lang)} {formatPlace(fromPlace)}{" "}
            {ui("taxiTo", lang)} {p.name}
          </Link>

          {desc && (
            <Box ml={3} mt={0.5}>
              
{expanded && desc && (
  <Typography color="textSecondary" variant="body2">
    {desc}
  </Typography>
)}



              <Link
                to="#"
                onClick={e => {
                  e.preventDefault();
                  setExpandedPopular(prev => ({
                    ...prev,
                    [p.slug]: !expanded
                  }));
                }}
                style={{ fontSize: 13 }}
              >
                {expanded ? "❌" : "👉"}
              </Link>
            </Box>
          )}
        </Box>
      );
    })}
  </>
)}



            {transfer.to.postcode && (
            <Box mt={3}>
              🚕 <Link to={`/transfer/${transfer.to.postcode}?lang=${lang}`}>
                {ui("moreHotelsDistrict", lang)} {transfer.to.postcode}
              </Link>
            </Box>
            )}
          </>
        )}
      </Box>

      {/* APP CTA */}
      <Box mt={6} textAlign="center">
        <Typography>{T.book}</Typography>
        <a href={APPLE_LINK}><img src={require("../assets/img/appstore.png")} height={48} /></a>
        <a href={PLAY_LINK}><img src={require("../assets/img/playstore.png")} height={48} /></a>
      </Box>

      {/* LANGUAGE FLAGS */}
      {/* ✅ LANGUAGE FLAGS (BOTTOM – RESTORED & GUARANTEED) */}
<Box
  mt={4}
  mb={2}
  display="flex"
  justifyContent="center"
  gap={1}
  flexWrap="wrap"
>
  {LANGUAGES.map(l => (
    <Link
      key={l.code}
      to={`?lang=${l.code}`}
      style={{ textDecoration: "none" }}
    >
      <Chip
        size="small"
        label={`${l.flag} ${l.label}`}
        clickable
      />
    </Link>
  ))}
</Box>


      <Footer />

      <Snackbar
        open={toast}
        autoHideDuration={3000}
        onClose={() => setToast(false)}
        /* ✅ CHANGED TEXT ONLY */
        message={ui("priceHint", lang)}
      />
       
     </>
    )}
  </div>
);
}